import React, { useEffect, useState, useCallback, useMemo } from "react";
import axios from "@/lib/axios";
import { API_BASE_URL } from "@/config";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  RefreshCw,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Filter,
  X,
  Download,
  Loader2
} from "lucide-react";

interface User {
  id: number;
  username: string;
  first_name: string;
  last_name: string;
}

export interface Tracker {
  id: number;
  verification_name: string;
  verification_code: string;
  is_mandatory: boolean;
  status: string;
  message: string;
  comment: string;
  need_manual_review: boolean;
  reason_for_manual_review: string;
  manual_review_done: boolean;
  manual_review_comment: string;
  manual_review_done_by: string | null;
  manual_review_done_at: string | null;
  created_at: string;
  updated_at: string;
}

type SortField = "id" | "verification_name" | "is_mandatory" | "status" | "updated_at" | "need_manual_review" | "manual_review_done" | "manual_review_done_by" | "manual_review_done_at";
type SortOrder = "asc" | "desc";

interface VerificationDebugProps {
  isEmbedded?: boolean;
  forcedUserId?: string;
}

const VerificationDebug: React.FC<VerificationDebugProps> = ({ isEmbedded = false, forcedUserId }) => {
  const [users, setUsers] = useState<User[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<string>("");

  // Sync with forcedUserId prop
  useEffect(() => {
    if (forcedUserId !== undefined) {
      setSelectedUserId(forcedUserId);
    }
  }, [forcedUserId]);
  const [trackers, setTrackers] = useState<Tracker[]>([]);
  const [generatingReport, setGeneratingReport] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fetchingUsers, setFetchingUsers] = useState(true);
  const [isFilterExpanded, setIsFilterExpanded] = useState(false);

  // Sorting state
  const [sortField, setSortField] = useState<SortField>("id");
  const [sortOrder, setSortOrder] = useState<SortOrder>("desc");

  // Filtering state
  const [filters, setFilters] = useState({
    verification: "",
    status: [] as string[],
    isMandatory: [] as boolean[],
    message: "",
    comment: "",
    updatedAt: "",
    needManualReview: [] as boolean[],
    manualReviewDone: [] as boolean[],
    reviewedBy: [] as string[],
  });

  const fetchUsers = async () => {
    try {
      const response = await axios.post(`${API_BASE_URL}/api/public/users/`, {});
      setUsers(response.data);
    } catch (error) {
      console.error("Failed to fetch users:", error);
    } finally {
      setFetchingUsers(false);
    }
  };

  const fetchTrackers = useCallback(async (userId: string) => {
    if (!userId) return;
    setLoading(true);
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/public/trackers/`,
        { user_id: parseInt(userId) }
      );
      setTrackers(response.data);
    } catch (error) {
      console.error("Failed to fetch trackers:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    if (selectedUserId) {
      fetchTrackers(selectedUserId);
    }
  }, [selectedUserId, fetchTrackers]);

  const handleRefresh = () => {
    if (selectedUserId) {
      fetchTrackers(selectedUserId);
    }
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  const toggleStatusFilter = (status: string) => {
    setFilters(prev => ({
      ...prev,
      status: prev.status.includes(status)
        ? prev.status.filter(s => s !== status)
        : [...prev.status, status]
    }));
  };

  const toggleMandatoryFilter = (val: boolean) => {
    setFilters(prev => ({
      ...prev,
      isMandatory: prev.isMandatory.includes(val)
        ? prev.isMandatory.filter(v => v !== val)
        : [...prev.isMandatory, val]
    }));
  };

  const toggleNeedManualReviewFilter = (val: boolean) => {
    setFilters(prev => ({
      ...prev,
      needManualReview: prev.needManualReview.includes(val)
        ? prev.needManualReview.filter(v => v !== val)
        : [...prev.needManualReview, val]
    }));
  };

  const toggleManualReviewDoneFilter = (val: boolean) => {
    setFilters(prev => ({
      ...prev,
      manualReviewDone: prev.manualReviewDone.includes(val)
        ? prev.manualReviewDone.filter(v => v !== val)
        : [...prev.manualReviewDone, val]
    }));
  };

  const toggleReviewedByFilter = (val: string) => {
    setFilters(prev => ({
      ...prev,
      reviewedBy: prev.reviewedBy.includes(val)
        ? prev.reviewedBy.filter(v => v !== val)
        : [...prev.reviewedBy, val]
    }));
  };

  // Extract unique reviewers from loaded trackers
  const availableReviewers = useMemo(() => {
    const reviewers = new Set<string>();
    trackers.forEach(t => {
      if (t.manual_review_done_by) {
        reviewers.add(t.manual_review_done_by);
      }
    });
    return Array.from(reviewers).sort();
  }, [trackers]);

  const filteredAndSortedTrackers = useMemo(() => {
    let result = [...trackers];

    // Filtering logic
    if (filters.verification) {
      const query = filters.verification.toLowerCase();
      result = result.filter(t =>
        t.verification_name.toLowerCase().includes(query) ||
        t.verification_code.toLowerCase().includes(query)
      );
    }
    if (filters.status.length > 0) {
      result = result.filter(t => filters.status.includes(t.status.toLowerCase()));
    }
    if (filters.isMandatory.length > 0) {
      result = result.filter(t => filters.isMandatory.includes(t.is_mandatory));
    }
    if (filters.needManualReview.length > 0) {
      result = result.filter(t => filters.needManualReview.includes(t.need_manual_review));
    }
    if (filters.manualReviewDone.length > 0) {
      result = result.filter(t => filters.manualReviewDone.includes(t.manual_review_done));
    }
    if (filters.reviewedBy.length > 0) {
      result = result.filter(t => t.manual_review_done_by && filters.reviewedBy.includes(t.manual_review_done_by));
    }
    if (filters.message) {
      const query = filters.message.toLowerCase();
      result = result.filter(t => t.message?.toLowerCase().includes(query));
    }
    if (filters.comment) {
      const query = filters.comment.toLowerCase();
      result = result.filter(t => t.comment?.toLowerCase().includes(query));
    }
    if (filters.updatedAt) {
      const query = filters.updatedAt.toLowerCase();
      result = result.filter(t =>
        new Date(t.updated_at).toLocaleString().toLowerCase().includes(query)
      );
    }

    // Sorting logic
    result.sort((a, b) => {
      let valA: any = a[sortField];
      let valB: any = b[sortField];

      if (sortField === "updated_at" || sortField === "manual_review_done_at") {
        valA = valA ? new Date(valA).getTime() : 0;
        valB = valB ? new Date(valB).getTime() : 0;
      }

      if (valA < valB) return sortOrder === "asc" ? -1 : 1;
      if (valA > valB) return sortOrder === "asc" ? 1 : -1;
      return 0;
    });

    return result;
  }, [trackers, sortField, sortOrder, filters]);

  const getStatusBadge = (status: string) => {
    const s = status.toLowerCase();

    const base =
      "inline-flex items-center justify-center whitespace-nowrap px-3 py-1 text-xs font-medium leading-tight rounded-full";

    switch (s) {
      case "passed":
      case "success":
      case "successful":
        return (
          <Badge className={`${base} bg-green-500 hover:bg-green-600 text-white border-none`}>
            Successful
          </Badge>
        );

      case "failed":
      case "rejected":
        return (
          <Badge className={`${base} bg-red-500 hover:bg-red-600 text-white border-none`}>
            Failed
          </Badge>
        );

      case "pending":
      case "waiting":
        return (
          <Badge className={`${base} bg-yellow-500 hover:bg-yellow-600 text-white border-none`}>
            Pending
          </Badge>
        );

      case "processing":
        return (
          <Badge className={`${base} bg-blue-500 hover:bg-blue-600 text-white border-none`}>
            Processing
          </Badge>
        );

      case "not_applicable":
      case "not applicable":
      case "na":
        return (
          <Badge className={`${base} bg-gray-400 hover:bg-gray-500 text-white border-none`}>
            Not Applicable
          </Badge>
        );

      default:
        return (
          <Badge className={`${base} border`}>
            {status}
          </Badge>
        );
    }
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return <ArrowUpDown className="ml-2 h-4 w-4" />;
    return sortOrder === "asc" ? <ArrowUp className="ml-2 h-4 w-4" /> : <ArrowDown className="ml-2 h-4 w-4" />;
  };

  const statusOptions = ["pending", "processing", "passed", "success", "successful", "failed", "not_applicable"];

  return (
    <div className={isEmbedded ? "w-full overflow-hidden p-1" : "min-h-screen bg-[#FAF9F6] py-10 px-4"}>
      <Card className={isEmbedded ? "border-none shadow-none bg-transparent" : ""}>
        <CardHeader className={`flex flex-row items-center justify-between space-y-0 ${isEmbedded ? "px-0 pb-4 pt-0" : "pb-7"}`}>
          {!isEmbedded ? (
            <>
              <CardTitle className="text-2xl font-bold">
                Verification Tracker Debug
              </CardTitle>
              <div className="flex items-center space-x-4">
                <Select onValueChange={setSelectedUserId} value={selectedUserId}>
                  <SelectTrigger className="w-[280px]">
                    <SelectValue placeholder={fetchingUsers ? "Loading users..." : "Select a user"} />
                  </SelectTrigger>
                  <SelectContent>
                    {users.map((user) => (
                      <SelectItem key={user.id} value={user.id.toString()}>
                        {user.id} ({user.first_name} {user.last_name})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={handleRefresh}
                  disabled={!selectedUserId || loading}
                >
                  <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
                </Button>
              </div>
            </>
          ) : (
            <div className="flex items-center justify-between w-full">
              <CardTitle className="text-lg font-bold text-gray-800">
                Verification Progress
              </CardTitle>
              <div className="flex items-center gap-2">
                {!isFilterExpanded && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setIsFilterExpanded(true)}
                    className="h-8 gap-2 rounded-lg border-intellend-green/20 text-intellend-green hover:bg-intellend-green/5 transition-all duration-300"
                  >
                    <Filter className="h-3.5 w-3.5" />
                    <span className="text-[11px] font-bold">Filter Trackers</span>
                    {(filters.verification || filters.status.length > 0 || filters.isMandatory.length > 0 || filters.needManualReview.length > 0 || filters.manualReviewDone.length > 0 || filters.reviewedBy.length > 0 || filters.message || filters.comment || filters.updatedAt) && (
                      <span className="flex h-1.5 w-1.5 rounded-full bg-intellend-gold animate-bounce" />
                    )}
                  </Button>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleRefresh}
                  disabled={!selectedUserId || loading}
                  className="h-8 gap-2 rounded-lg border-intellend-green/20 text-intellend-green hover:bg-intellend-green/5"
                >
                  <RefreshCw className={`h-3 w-3 ${loading ? "animate-spin" : ""}`} />
                  <span className="text-[11px] font-bold">Sync Data</span>
                </Button>
              </div>
            </div>
          )}
        </CardHeader>
        <CardContent>
          {selectedUserId ? (
            <div className="space-y-4">
              {/* Filters UI */}
              {isFilterExpanded && (
                <div className="bg-slate-50 p-4 rounded-lg border relative animate-in fade-in slide-in-from-top-2 duration-200">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setIsFilterExpanded(false)}
                    className="absolute top-2 right-2 h-8 w-8 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-full transition-colors"
                  >
                    <X className="h-5 w-5" />
                  </Button>

                  <div className="flex flex-wrap gap-4 items-end mt-2">
                    <div className="grid w-full max-w-[200px] items-center gap-1.5">
                      <Label htmlFor="verification" className="text-xs">Verification</Label>
                      <Input
                        id="verification"
                        placeholder="Name/code..."
                        value={filters.verification}
                        onChange={(e) => setFilters(prev => ({ ...prev, verification: e.target.value }))}
                        className="h-8 text-xs"
                      />
                    </div>

                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" size="sm" className="h-8 text-xs">
                          <Filter className="mr-2 h-3 w-3" />
                          Status {filters.status.length > 0 && `(${filters.status.length})`}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-56" align="start">
                        <div className="space-y-4">
                          <h4 className="font-medium leading-none">Filter Status</h4>
                          <div className="space-y-2">
                            {statusOptions.map(status => (
                              <div key={status} className="flex items-center space-x-2">
                                <Checkbox
                                  id={`status-${status}`}
                                  checked={filters.status.includes(status)}
                                  onCheckedChange={() => toggleStatusFilter(status)}
                                />
                                <Label htmlFor={`status-${status}`} className="capitalize">{status}</Label>
                              </div>
                            ))}
                          </div>
                        </div>
                      </PopoverContent>
                    </Popover>

                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" size="sm" className="h-8 text-xs">
                          <Filter className="mr-2 h-3 w-3" />
                          Mandatory {filters.isMandatory.length > 0 && `(${filters.isMandatory.length})`}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-56" align="start">
                        <div className="space-y-4">
                          <h4 className="font-medium leading-none">Filter Mandatory</h4>
                          <div className="space-y-2">
                            <div className="flex items-center space-x-2">
                              <Checkbox
                                id="mand-yes"
                                checked={filters.isMandatory.includes(true)}
                                onCheckedChange={() => toggleMandatoryFilter(true)}
                              />
                              <Label htmlFor="mand-yes">Yes</Label>
                            </div>
                            <div className="flex items-center space-x-2">
                              <Checkbox
                                id="mand-no"
                                checked={filters.isMandatory.includes(false)}
                                onCheckedChange={() => toggleMandatoryFilter(false)}
                              />
                              <Label htmlFor="mand-no">No</Label>
                            </div>
                          </div>
                        </div>
                      </PopoverContent>
                    </Popover>

                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" size="sm" className="h-8 text-xs">
                          <Filter className="mr-2 h-3 w-3" />
                          Manual Review {filters.needManualReview.length > 0 && `(${filters.needManualReview.length})`}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-56" align="start">
                        <div className="space-y-4">
                          <h4 className="font-medium leading-none">Need Manual Review?</h4>
                          <div className="space-y-2">
                            <div className="flex items-center space-x-2">
                              <Checkbox
                                id="need-mr-yes"
                                checked={filters.needManualReview.includes(true)}
                                onCheckedChange={() => toggleNeedManualReviewFilter(true)}
                              />
                              <Label htmlFor="need-mr-yes">Yes</Label>
                            </div>
                            <div className="flex items-center space-x-2">
                              <Checkbox
                                id="need-mr-no"
                                checked={filters.needManualReview.includes(false)}
                                onCheckedChange={() => toggleNeedManualReviewFilter(false)}
                              />
                              <Label htmlFor="need-mr-no">No</Label>
                            </div>
                          </div>
                        </div>
                      </PopoverContent>
                    </Popover>

                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" size="sm" className="h-8 text-xs">
                          <Filter className="mr-2 h-3 w-3" />
                          Review Done {filters.manualReviewDone.length > 0 && `(${filters.manualReviewDone.length})`}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-56" align="start">
                        <div className="space-y-4">
                          <h4 className="font-medium leading-none">Review Done?</h4>
                          <div className="space-y-2">
                            <div className="flex items-center space-x-2">
                              <Checkbox
                                id="mr-done-yes"
                                checked={filters.manualReviewDone.includes(true)}
                                onCheckedChange={() => toggleManualReviewDoneFilter(true)}
                              />
                              <Label htmlFor="mr-done-yes">Yes</Label>
                            </div>
                            <div className="flex items-center space-x-2">
                              <Checkbox
                                id="mr-done-no"
                                checked={filters.manualReviewDone.includes(false)}
                                onCheckedChange={() => toggleManualReviewDoneFilter(false)}
                              />
                              <Label htmlFor="mr-done-no">No</Label>
                            </div>
                          </div>
                        </div>
                      </PopoverContent>
                    </Popover>

                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" size="sm" className="h-8 text-xs">
                          <Filter className="mr-2 h-3 w-3" />
                          Reviewed By {filters.reviewedBy.length > 0 && `(${filters.reviewedBy.length})`}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-56" align="start">
                        <div className="space-y-4 p-1">
                          <h4 className="font-medium leading-none mb-3 px-2">Filter Reviewer</h4>
                          <div className="space-y-2 max-h-48 overflow-y-auto px-2">
                            {availableReviewers.length > 0 ? availableReviewers.map(reviewer => (
                              <div key={reviewer} className="flex items-center space-x-2">
                                <Checkbox
                                  id={`reviewer-${reviewer}`}
                                  checked={filters.reviewedBy.includes(reviewer)}
                                  onCheckedChange={() => toggleReviewedByFilter(reviewer)}
                                />
                                <Label htmlFor={`reviewer-${reviewer}`}>{reviewer}</Label>
                              </div>
                            )) : (
                              <div className="text-xs text-muted-foreground">No reviewers found in current list</div>
                            )}
                          </div>
                        </div>
                      </PopoverContent>
                    </Popover>


                    <div className="grid w-full max-w-[150px] items-center gap-1.5">
                      <Label htmlFor="message" className="text-xs">Message</Label>
                      <Input
                        id="message"
                        placeholder="Message..."
                        value={filters.message}
                        onChange={(e) => setFilters(prev => ({ ...prev, message: e.target.value }))}
                        className="h-8 text-xs"
                      />
                    </div>

                    <div className="grid w-full max-w-[150px] items-center gap-1.5">
                      <Label htmlFor="comment" className="text-xs">Comment</Label>
                      <Input
                        id="comment"
                        placeholder="Comment..."
                        value={filters.comment}
                        onChange={(e) => setFilters(prev => ({ ...prev, comment: e.target.value }))}
                        className="h-8 text-xs"
                      />
                    </div>

                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 text-xs text-red-500 hover:text-red-700 hover:bg-red-50"
                      onClick={() => setFilters({
                        verification: "",
                        status: [],
                        isMandatory: [],
                        message: "",
                        comment: "",
                        updatedAt: "",
                        needManualReview: [],
                        manualReviewDone: [],
                        reviewedBy: [],
                      })}
                    >
                      <X className="mr-1 h-3 w-3" />
                      Clear
                    </Button>

                    <Button
                      variant="outline"
                      size="sm"
                      className="h-8 text-xs ml-auto"
                      disabled={generatingReport}
                      onClick={async () => {
                        if (!selectedUserId) return;
                        setGeneratingReport(true);
                        try {
                          const response = await axios.post(
                            `/api/admin/download_report/`,
                            { user_id: selectedUserId },
                            { responseType: 'blob' }
                          );
                          const url = window.URL.createObjectURL(new Blob([response.data]));
                          const link = document.createElement('a');
                          link.href = url;
                          link.setAttribute('download', `verification_report_${selectedUserId}.pdf`);
                          document.body.appendChild(link);
                          link.click();
                          link.parentNode?.removeChild(link);
                        } catch (error: any) {
                          console.error("Failed to download report", error);
                          let errorMessage = "Failed to download report";
                          if (error.response?.data instanceof Blob) {
                            try {
                              const text = await error.response.data.text();
                              const json = JSON.parse(text);
                              if (json.error) errorMessage = json.error;
                            } catch (e) {
                              // ignore
                            }
                          }
                          toast.error(errorMessage);
                        } finally {
                          setGeneratingReport(false);
                        }
                      }}
                    >
                      {generatingReport ? (
                        <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                      ) : (
                        <Download className="mr-1 h-3 w-3" />
                      )}
                      {generatingReport ? "Generating..." : "Report"}
                    </Button>
                  </div>
                </div>
              )}

              <div className="rounded-md border overflow-auto max-h-[75vh]">
                <table className="w-full caption-bottom text-sm">
                  <TableHeader>
                    <TableRow>
                      <TableHead
                        className="w-[80px] cursor-pointer hover:bg-slate-50 transition-colors"
                        onClick={() => handleSort("id")}
                      >
                        <div className="flex items-center">
                          ID <SortIcon field="id" />
                        </div>
                      </TableHead>
                      <TableHead
                        className="cursor-pointer hover:bg-slate-50 transition-colors"
                        onClick={() => handleSort("verification_name")}
                      >
                        <div className="flex items-center">
                          Verification <SortIcon field="verification_name" />
                        </div>
                      </TableHead>
                      <TableHead
                        className="cursor-pointer hover:bg-slate-50 transition-colors"
                        onClick={() => handleSort("is_mandatory")}
                      >
                        <div className="flex items-center">
                          Mandatory <SortIcon field="is_mandatory" />
                        </div>
                      </TableHead>
                      <TableHead
                        className="cursor-pointer hover:bg-slate-50 transition-colors"
                        onClick={() => handleSort("status")}
                      >
                        <div className="flex items-center">
                          Status <SortIcon field="status" />
                        </div>
                      </TableHead>

                      <TableHead className="min-w-[150px]">Message</TableHead>
                      <TableHead className="min-w-[150px]">Comment</TableHead>
                      <TableHead
                        className="text-right w-[180px] cursor-pointer hover:bg-slate-50 transition-colors"
                        onClick={() => handleSort("updated_at")}
                      >
                        <div className="flex items-center justify-end">
                          Updated At <SortIcon field="updated_at" />
                        </div>
                      </TableHead>
                      <TableHead
                        className="cursor-pointer hover:bg-slate-50 transition-colors"
                        onClick={() => handleSort("need_manual_review")}
                      >
                        <div className="flex items-center">
                          Need Review <SortIcon field="need_manual_review" />
                        </div>
                      </TableHead>
                      <TableHead
                        className="cursor-pointer hover:bg-slate-50 transition-colors"
                        onClick={() => handleSort("manual_review_done")}
                      >
                        <div className="flex items-center">
                          Review Done <SortIcon field="manual_review_done" />
                        </div>
                      </TableHead>
                      <TableHead
                        className="cursor-pointer hover:bg-slate-50 transition-colors"
                        onClick={() => handleSort("manual_review_done_by")}
                      >
                        <div className="flex items-center">
                          Reviewed By <SortIcon field="manual_review_done_by" />
                        </div>
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell colSpan={10} className="h-24 text-center">
                          Loading trackers...
                        </TableCell>
                      </TableRow>
                    ) : filteredAndSortedTrackers.length > 0 ? (
                      filteredAndSortedTrackers.map((tracker) => (
                        <TableRow key={tracker.id}>
                          <TableCell className="font-medium">{tracker.id}</TableCell>
                          <TableCell>
                            <div className="font-semibold">{tracker.verification_name}</div>
                            <div className="text-xs text-muted-foreground">
                              {tracker.verification_code}
                            </div>
                          </TableCell>
                          <TableCell>
                            {tracker.is_mandatory ? (
                              <Badge variant="outline" className="text-blue-500 border-blue-500">Yes</Badge>
                            ) : (
                              <Badge variant="outline" className="text-gray-400">No</Badge>
                            )}
                          </TableCell>
                          <TableCell>{getStatusBadge(tracker.status)}</TableCell>

                          <TableCell className="whitespace-normal break-words max-w-[250px]">
                            {tracker.message || "-"}
                          </TableCell>
                          <TableCell className="whitespace-normal break-words max-w-[250px]">
                            {tracker.comment || "-"}
                          </TableCell>
                          <TableCell className="text-right text-xs whitespace-normal w-[180px]">
                            {new Date(tracker.updated_at).toLocaleString()}
                          </TableCell>

                          <TableCell>
                            {tracker.need_manual_review ? (
                              <div className="flex items-center gap-1 text-amber-600 font-medium">
                                Yes
                                {tracker.reason_for_manual_review && (
                                  <span title={tracker.reason_for_manual_review} className="cursor-help text-xs text-gray-500 underline ml-1">(Reason)</span>
                                )}
                              </div>
                            ) : (
                              <span className="text-gray-400">No</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {tracker.manual_review_done ? (
                              <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">Yes</Badge>
                            ) : (
                              <span className="text-gray-400">No</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {tracker.manual_review_done_by ? (
                              <div className="flex flex-col">
                                <span className="font-medium">{tracker.manual_review_done_by}</span>
                                {tracker.manual_review_done_at && (
                                  <span className="text-[10px] text-gray-500">
                                    {new Date(tracker.manual_review_done_at).toLocaleDateString()}
                                  </span>
                                )}
                                {tracker.manual_review_comment && (
                                  <span title={tracker.manual_review_comment} className="text-[10px] text-gray-400 truncate max-w-[100px] cursor-help">
                                    {tracker.manual_review_comment}
                                  </span>
                                )}
                              </div>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={7} className="h-24 text-center">
                          No trackers found matching filters.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </table>
              </div>
            </div>
          ) : (
            <div className="flex h-[400px] items-center justify-center rounded-md border border-dashed">
              <p className="text-muted-foreground text-sm">
                Please select a user to view their verification status.
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default VerificationDebug;
